import { z } from "zod";
import { QuestionType, SPEAKING_WORDS_PER_MINUTE } from "./constants";

// Answer frameworks: the ordered structure candidates are coached against for
// each question type. STARR backs behavioural questions ("tell me about a time
// you..."), CARL backs situational questions ("what would you do if...").

export const BehaviouralKey = {
  Situation: "situation",
  Task: "task",
  Action: "action",
  Result: "result",
  Reflection: "reflection",
} as const;
export type BehaviouralKey = (typeof BehaviouralKey)[keyof typeof BehaviouralKey];

export const SituationalKey = {
  Context: "context",
  Action: "action",
  Result: "result",
  Learning: "learning",
} as const;
export type SituationalKey = (typeof SituationalKey)[keyof typeof SituationalKey];

// A target length range, in whole seconds. The prose in each description and
// in totalTime is generated from these numbers, so the two can't drift.
export const TargetSecondsSchema = z.object({
  min: z.number().int().positive(),
  max: z.number().int().positive(),
});
export type TargetSeconds = z.infer<typeof TargetSecondsSchema>;

export const FrameworkComponentSchema = z.object({
  key: z.string(),
  label: z.string(),
  description: z.string(),
  targetSeconds: TargetSecondsSchema,
});
export type FrameworkComponent = z.infer<typeof FrameworkComponentSchema>;

export const StructureAnchorSchema = z.object({
  score: z.number(),
  label: z.string(),
  description: z.string(),
});
export type StructureAnchor = z.infer<typeof StructureAnchorSchema>;

export const StructureRubricSchema = z.object({
  totalTime: z.string(),
  totalSeconds: TargetSecondsSchema,
  criteria: z.array(z.string()),
  anchors: z.array(StructureAnchorSchema),
  strongIndicators: z.array(z.string()),
  weakIndicators: z.array(z.string()),
});
export type StructureRubric = z.infer<typeof StructureRubricSchema>;

// Criteria and indicators are identical for both STARR and CARL.
const STRUCTURE_CRITERIA = [
  "Whether the candidate answered the question asked",
  "Whether the answer was logically organized",
  "Whether the candidate used an appropriate structure",
  "Whether the answer was specific enough to evaluate",
  "Whether the candidate avoided rambling, over-explaining, or becoming too vague",
];

const STRUCTURE_ANCHORS: StructureAnchor[] = [
  { score: 0,  label: "Absent",    description: "No clear structure. Answer is confusing, incomplete, or does not respond to the question asked." },
  { score: 5,  label: "Weak",      description: "Some relevant content, but the answer is disorganized, overly vague, too brief, or difficult to follow." },
  { score: 10, label: "Adequate",  description: "Basic structure is present. Candidate covers most required elements, but some parts are thin, unclear, out of order, or underdeveloped." },
  { score: 15, label: "Proficient", description: "Answer is clear, organized, and easy to follow. Candidate uses the framework effectively and provides enough detail to assess the competency." },
  { score: 20, label: "Exemplary", description: "Answer is polished, focused, well-paced, and highly assessable. Candidate uses structure naturally without sounding scripted, balances detail with concision, and clearly connects actions to reasoning and outcome." },
];

const STRUCTURE_STRONG_INDICATORS = [
  "Starts with a relevant setup",
  "Makes their own role clear",
  "Explains their decision-making process clearly",
  "Uses first-person language when describing their actions",
  "Provides enough detail without overwhelming the answer",
  "Connects actions to policy, ethics, risk, stakeholders, or outcome",
  "Ends with a result, reflection, or follow-up",
];

const STRUCTURE_WEAK_INDICATORS = [
  "Includes background that doesn't bear on their role or decisions",
  "Speaks in generalities rather than giving a specific example",
  "Describes what \"we\" did without explaining their own role",
  "Jumps straight to the outcome without explaining the decision process",
  "Lists actions without explaining reasoning",
  "Rambles, repeats themselves, or loses the thread of the answer",
  "Provides a memorized structure that sounds polished but lacks substance",
  "Ends without a clear result or reflection",
  "Fails to answer the question asked",
];

const rangeText = ({ min, max }: TargetSeconds, plural: boolean): string => {
  const s = plural ? "s" : "";
  return min % 60 === 0 && max % 60 === 0 ? `${min / 60}–${max / 60} minute${s}` : `${min}–${max} second${s}`;
};

// "15–45 seconds", or "3–4 minutes" when both ends are whole minutes.
export const formatTargetRange = (target: TargetSeconds): string => rangeText(target, true);

// "3–4 minute", as in "its 3–4 minute target".
const formatTargetRangeAdjective = (target: TargetSeconds): string => rangeText(target, false);

// A component whose description states its own target length.
const timedComponent = <Key extends string>(component: {
  key: Key;
  label: string;
  targetSeconds: TargetSeconds;
  describe: (targetTime: string) => string;
}) => ({
  key: component.key,
  label: component.label,
  targetSeconds: component.targetSeconds,
  description: component.describe(formatTargetRange(component.targetSeconds)),
});

// Each framework's components are an ordered array — the sequence is part of
// the framework (S→T→A→R→R, C→A→R→L) and is what the coach walks through.
// Per-type component schemas pin `key` to the framework's own enum so the
// structure cannot drift from its defined components.
export const BehaviouralFrameworkSchema = z.object({
  type: z.literal(QuestionType.BEHAVIOURAL),
  name: z.literal("STARR"),
  components: z
    .array(FrameworkComponentSchema.extend({ key: z.nativeEnum(BehaviouralKey) }))
    .nonempty(),
  structureRubric: StructureRubricSchema,
});
export type BehaviouralFramework = z.infer<typeof BehaviouralFrameworkSchema>;

export const SituationalFrameworkSchema = z.object({
  type: z.literal(QuestionType.SITUATIONAL),
  name: z.literal("CARL"),
  components: z
    .array(FrameworkComponentSchema.extend({ key: z.nativeEnum(SituationalKey) }))
    .nonempty(),
  structureRubric: StructureRubricSchema,
});
export type SituationalFramework = z.infer<typeof SituationalFrameworkSchema>;

export const AnswerFrameworkSchema = z.discriminatedUnion("type", [
  BehaviouralFrameworkSchema,
  SituationalFrameworkSchema,
]);
export type AnswerFramework = z.infer<typeof AnswerFrameworkSchema>;

const STARR_TOTAL_SECONDS: TargetSeconds = { min: 360, max: 420 };
const CARL_TOTAL_SECONDS: TargetSeconds = { min: 120, max: 180 };

export const BEHAVIOURAL_FRAMEWORK: BehaviouralFramework =
  BehaviouralFrameworkSchema.parse({
    type: QuestionType.BEHAVIOURAL,
    name: "STARR",
    components: [
      timedComponent({
        key: BehaviouralKey.Situation,
        label: "Situation",
        targetSeconds: { min: 60, max: 120 },
        describe: (time) =>
          `The situational problem or event the candidate was operating in (${time}). They should paint a picture for the interview panel as if it knows nothing about the situation and how they relate to it, capturing the gravity and/or urgency of the situation and keeping the setup to what the panel needs.`,
      }),
      timedComponent({
        key: BehaviouralKey.Task,
        label: "Task",
        targetSeconds: { min: 15, max: 45 },
        describe: (time) =>
          `The task(s) the candidate had to complete (${time}). Should distinguish their personal responsibility from the broader team or organisational goal.`,
      }),
      timedComponent({
        key: BehaviouralKey.Action,
        label: "Action",
        targetSeconds: { min: 180, max: 240 },
        describe: (time) =>
          `The specific steps, techniques, and skills the candidate personally applied to accomplish the task(s) (${time} — the most important part of the answer, where the majority of the time should be spent). Look for first-person ownership and a clear account of what the candidate did, as distinct from what the team did.`,
      }),
      timedComponent({
        key: BehaviouralKey.Result,
        label: "Result",
        targetSeconds: { min: 15, max: 45 },
        describe: (time) =>
          `The end result(s) of the candidate's actions (${time}). Should be a positive result — specific and, where possible, measurable. Look for honest attribution, with credit taken appropriately and neither over- nor under-claimed.`,
      }),
      timedComponent({
        key: BehaviouralKey.Reflection,
        label: "Reflection",
        targetSeconds: { min: 15, max: 45 },
        describe: (time) =>
          `What the candidate learned or would do differently (${time}). Not always appropriate for every answer, but valued when it demonstrates an ability to learn and grow from the experience.`,
      }),
    ],
    structureRubric: {
      totalTime: formatTargetRange(STARR_TOTAL_SECONDS),
      totalSeconds: STARR_TOTAL_SECONDS,
      criteria: STRUCTURE_CRITERIA,
      anchors: STRUCTURE_ANCHORS,
      strongIndicators: STRUCTURE_STRONG_INDICATORS,
      weakIndicators: STRUCTURE_WEAK_INDICATORS,
    },
  });

export const SITUATIONAL_FRAMEWORK: SituationalFramework =
  SituationalFrameworkSchema.parse({
    type: QuestionType.SITUATIONAL,
    name: "CARL",
    components: [
      timedComponent({
        key: SituationalKey.Context,
        label: "Context",
        targetSeconds: { min: 15, max: 30 },
        describe: (time) =>
          `The situation presented in the question (${time}). The candidate should demonstrate that they understand the issue, the competing priorities, and any risks, policies, or stakeholders involved before explaining their response.`,
      }),
      timedComponent({
        key: SituationalKey.Action,
        label: "Action",
        targetSeconds: { min: 60, max: 120 },
        describe: (time) =>
          `The specific steps the candidate would take to address the situation (${time} — the most important part of the answer). Should explain their decision-making process, priorities, communication approach, and any actions taken to ensure safety, integrity, professionalism, policy compliance, and accountability. Concrete, sequenced plans ('I would first..., then...') are stronger than vague intentions ('I would communicate').`,
      }),
      timedComponent({
        key: SituationalKey.Result,
        label: "Result",
        targetSeconds: { min: 15, max: 30 },
        describe: (time) =>
          `The expected outcome of the candidate's proposed actions (${time}). Should be realistic, positive, and aligned with organisational objectives.`,
      }),
      timedComponent({
        key: SituationalKey.Learning,
        label: "Learning",
        targetSeconds: { min: 15, max: 30 },
        describe: (time) =>
          `What the candidate would learn from the experience, or how the situation reinforces professional standards, continuous improvement, or future decision-making (${time}).`,
      }),
    ],
    structureRubric: {
      totalTime: formatTargetRange(CARL_TOTAL_SECONDS),
      totalSeconds: CARL_TOTAL_SECONDS,
      criteria: STRUCTURE_CRITERIA,
      anchors: STRUCTURE_ANCHORS,
      strongIndicators: STRUCTURE_STRONG_INDICATORS,
      weakIndicators: STRUCTURE_WEAK_INDICATORS,
    },
  });

export const FRAMEWORKS: Record<QuestionType, AnswerFramework> = {
  [QuestionType.BEHAVIOURAL]: BEHAVIOURAL_FRAMEWORK,
  [QuestionType.SITUATIONAL]: SITUATIONAL_FRAMEWORK,
};

// Estimated speaking time from a component's word count — not how long the
// candidate took to type it. Computed on read (never persisted), so tuning
// SPEAKING_WORDS_PER_MINUTE later needs no backfill.
export const estimateSpeakingMs = (wordCount: number): number =>
  Math.round((wordCount / SPEAKING_WORDS_PER_MINUTE) * 60_000);

export interface SectionTiming {
  ms: number;
  estimated: boolean;
}

// Which timing to show for a framework component: the real measured duration
// when the answer came from a voice session, otherwise the word-count estimate.
// Shared (rather than repeated per view) so the candidate and admin surfaces
// can never disagree on which source wins for the same response — they render
// the result differently, but must choose it identically. Returns null when
// neither measure is available, e.g. rows persisted before either field existed.
// Typed structurally against StructureComponentScore's timing fields to keep
// this module free of a schemas.ts import.
export const sectionTiming = (component: {
  wordCount?: number;
  actualDurationMs?: number;
}): SectionTiming | null => {
  if (component.actualDurationMs != null) {
    return { ms: component.actualDurationMs, estimated: false };
  }
  if (component.wordCount != null) {
    return { ms: estimateSpeakingMs(component.wordCount), estimated: true };
  }
  return null;
};

export const countWords = (text: string): number =>
  text.trim().split(/\s+/).filter(Boolean).length;

const formatMsAsClock = (ms: number): string => {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const LengthVerdict = {
  Short: "short",
  OnTarget: "onTarget",
  Long: "long",
} as const;
export type LengthVerdict = (typeof LengthVerdict)[keyof typeof LengthVerdict];

// Off target only when well outside the range, so ordinary variation around
// it never counts: long is over 1.5× the top, short is under half the bottom.
const LONG_FACTOR = 1.5;
const SHORT_FACTOR = 0.5;

const classifyLength = (ms: number, target: TargetSeconds): LengthVerdict => {
  if (ms > target.max * 1000 * LONG_FACTOR) return LengthVerdict.Long;
  if (ms < target.min * 1000 * SHORT_FACTOR) return LengthVerdict.Short;
  return LengthVerdict.OnTarget;
};

/**
 * Each component's and the whole answer's length against the framework's
 * targets. A component whose span wasn't measured on its own (its startMarker
 * didn't resolve, so its time is a share of a neighbour's) gets no verdict:
 * there is no real figure to judge, so nothing may be said or scored about it.
 */
export const classifyAnswerLengths = (
  framework: AnswerFramework,
  components: readonly { key: string; ms: number; resolved: boolean }[],
  totalMs: number
): { total: LengthVerdict; components: Partial<Record<string, LengthVerdict>> } => ({
  total: classifyLength(totalMs, framework.structureRubric.totalSeconds),
  components: Object.fromEntries(
    components.flatMap((c) => {
      const target = framework.components.find((f) => f.key === c.key)?.targetSeconds;
      return target && c.resolved ? [[c.key, classifyLength(c.ms, target)]] : [];
    })
  ),
});

// "about 0:40", or "an estimated 0:40" for a typed answer's word-count figure.
const describeTiming = (timing: SectionTiming): string =>
  `${timing.estimated ? "an estimated" : "about"} ${formatMsAsClock(timing.ms)}`;

/**
 * The code-written sentence for an off-target component, e.g. "At about 0:40,
 * your Action was well under its 3–4 minute target." Null when on target.
 * The only length wording a candidate sees in a scored component.
 */
export const componentLengthSentence = (
  component: Pick<FrameworkComponent, "label" | "targetSeconds">,
  timing: SectionTiming,
  verdict: LengthVerdict
): string | null => {
  const target = `its ${formatTargetRangeAdjective(component.targetSeconds)} target`;
  const at = `At ${describeTiming(timing)}, your ${component.label}`;
  if (verdict === LengthVerdict.Short) return `${at} was well under ${target}.`;
  if (verdict === LengthVerdict.Long) return `${at} was well over ${target}.`;
  return null;
};

/** componentLengthSentence for the whole answer, appended to structureRationale. */
export const totalLengthSentence = (
  framework: AnswerFramework,
  timing: SectionTiming,
  verdict: LengthVerdict
): string | null => {
  const target = `the ${formatTargetRangeAdjective(framework.structureRubric.totalSeconds)} target`;
  const at = `At ${describeTiming(timing)}, your whole answer`;
  if (verdict === LengthVerdict.Short) return `${at} was well under ${target}.`;
  if (verdict === LengthVerdict.Long) return `${at} was well over ${target}.`;
  return null;
};

const formatTargetClock = ({ min, max }: TargetSeconds): string =>
  `${formatMsAsClock(min * 1000)}–${formatMsAsClock(max * 1000)}`;

/**
 * One <answer_length> line, e.g. "Situation: about 0:18 — too short (well
 * under its 1:00–2:00 target)". Spelled out in both directions: given a bare
 * "well under", the coach sometimes answered "too long?" with "yes".
 */
export const answerLengthLine = (
  label: string,
  timing: SectionTiming,
  target: TargetSeconds,
  verdict: LengthVerdict
): string => {
  const range = formatTargetClock(target);
  const verdictText =
    verdict === LengthVerdict.Short
      ? `too short (well under its ${range} target)`
      : verdict === LengthVerdict.Long
        ? `too long (well over its ${range} target)`
        : `within the normal range (target ${range}), neither too long nor too short`;
  return `${label}: about ${formatMsAsClock(timing.ms)} — ${verdictText}`;
};

// The whole answer's line of an <answer_length> block, from answerTiming.
// Computed in code so the model never estimates an answer's length.
export const buildAnswerLengthFact = (framework: AnswerFramework, total: SectionTiming): string => {
  const source = total.estimated
    ? "Estimated from the typed answer's word count at a typical speaking pace"
    : "Measured from the voice session";
  const verdict = classifyAnswerLengths(framework, [], total.ms).total;
  return `${source}.\n${answerLengthLine(
    "Whole answer",
    total,
    framework.structureRubric.totalSeconds,
    verdict
  )}`;
};

// The whole answer's timing: the real voice span when there is one, otherwise
// the word-count estimate.
export const answerTiming = (answer: string, totalVoiceDurationMs?: number): SectionTiming =>
  totalVoiceDurationMs != null
    ? { ms: totalVoiceDurationMs, estimated: false }
    : { ms: estimateSpeakingMs(countWords(answer)), estimated: true };

export const wrapAnswerLength = (body: string): string => `<answer_length>\n${body}\n</answer_length>`;

// Stands in for the per-component lines when there are none. An instruction,
// not a statement of what's missing, which the coach would read back.
export const NO_COMPONENT_TIMING_INSTRUCTION =
  "If the candidate asks how long one part of their answer was, make no length claim about it and say nothing about what you have, can or can't measure — talk about what that part covered instead.";

// What a live voice session is told at Stop: the whole answer's measured time.
// The per-component breakdown follows once Evaluate has scored the answer.
export const buildStopAnswerLength = (framework: AnswerFramework, totalMs: number): string =>
  wrapAnswerLength(
    `${buildAnswerLengthFact(framework, { ms: totalMs, estimated: false })}\n${NO_COMPONENT_TIMING_INSTRUCTION}`
  );
